import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { PRICE_EAT_FONTS, slugFont } from './priceEatFonts';
import { metrics, posToScreen, screenToPos, inView, quadrantVideoTransform, num } from './priceEatRender';

const IP = `${process.env.REACT_APP_IP}:${process.env.REACT_APP_PORT}`;
const IP2 = `${process.env.REACT_APP_IP2}:${process.env.REACT_APP_PORT2}`;
const Q = ['TL', 'TR', 'BL', 'BR'];

const BLANK = {
    Id: '', client_id: '', name: '', price: '', dispo: 'yes', display_id: '',
    id_product_display: 0, editable_price: 'yes', editable_stock: 'yes',
    display_videowall: '0', id_pdv: '', position_x: '0', position_y: '0',
    font_size: '48', font_name: 'roboto_regular', font_color: '#ffffff',
    indispo_name: 'Indisponible', position_icon_x: '0', position_icon_y: '0',
    width_icon: '60', token: '', value: '', linked_article_id: 0,
};

// Luminance relative (0=noir, 1=blanc) d'une couleur hex — pour l'alerte de lisibilité.
function luminance(hex) {
    const c = String(hex || '').replace('#', '');
    if (c.length < 6) return 1;
    const r = parseInt(c.slice(0, 2), 16) / 255, g = parseInt(c.slice(2, 4), 16) / 255, b = parseInt(c.slice(4, 6), 16) / 255;
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

// Rendu d'un prix (entier + décimales/€), sans dangerouslySetInnerHTML.
function PriceLabel({ price, fscale, fontSize, taille = 1, euro = true }) {
    const intSize = (num(fontSize, 24) / 2) * fscale;
    const decSize = (num(fontSize, 24) * taille / 2) * fscale;
    const s = (isFinite(+price) ? +price : 0).toFixed(2);
    const i = s.indexOf('.');
    const e = euro ? '€' : '';
    if (i >= 0) {
        return (<>
            <span style={{ fontSize: intSize }}>{s.slice(0, i)}</span>
            <span style={{ fontSize: decSize }}>{s.slice(i) + e}</span>
        </>);
    }
    return <span style={{ fontSize: intSize }}>{s + e}</span>;
}

/** Éditeur WYSIWYG price-eat. Remplace ModalCreateArticle / ModalModifyArticle. */
export default function PriceEditor({ isVisible, onClose, mode, article, allArticles = [], videoFiltered = [], wallGroups = [], orientationMap = {}, idPdv, clientId, onSaved }) {
    const token = localStorage.getItem('token');
    const [values, setValues] = useState(BLANK);
    const [fontList, setFontList] = useState(PRICE_EAT_FONTS);
    const [videoNat, setVideoNat] = useState({ w: 0, h: 0 });
    const [videoErr, setVideoErr] = useState(false);
    const [cropOn, setCropOn] = useState(false);
    const [quadrant, setQuadrant] = useState(0);
    const [stage, setStage] = useState({ w: 0, h: 0 });
    const [saving, setSaving] = useState(false);
    const [advanced, setAdvanced] = useState(false);   // Simple / Avancé
    const [preview, setPreview] = useState(false);      // Aperçu réel (sans repères éditeur)
    const [grid, setGrid] = useState(false);            // Grille magnétique
    // Mode d'édition INTERNE : peut passer de 'create' à 'edit' quand on clique un prix existant
    // directement sur la vidéo (sélection au clic, sans repasser par la liste d'articles).
    const [editMode, setEditMode] = useState(mode);
    const stageRef = useRef(null);
    const fontFileRef = useRef(null);
    const valuesRef = useRef(values); valuesRef.current = values;
    const histRef = useRef({ past: [], future: [] });
    const initialRef = useRef('');
    // Raccourcis clavier au niveau FENÊTRE : l'ancien onKeyDown sur le div racine ne recevait
    // plus rien dès que le focus partait (après un drag, un clic vidéo…). Les flèches marchent
    // désormais tout le temps — sauf pendant la saisie dans un champ (garde sur e.target).
    const onKeyRef = useRef(null);

    // (Ré)initialisation à l'ouverture
    useEffect(() => {
        if (!isVisible) return;
        const init = (mode === 'edit' && article) ? { ...BLANK, ...article } : { ...BLANK, id_pdv: idPdv, client_id: clientId };
        setValues(init);
        setEditMode(mode);
        initialRef.current = JSON.stringify(init);
        histRef.current = { past: [], future: [] };
    }, [isVisible, mode, article, idPdv, clientId]);

    const displayId = values.display_id;
    const videoUrl = useMemo(() => {
        const v = videoFiltered.find(x => String(x.id) === String(displayId));
        return v ? v.url : '';
    }, [videoFiltered, displayId]);

    const displayIds = [...new Set(videoFiltered.map(v => v.id).filter(Boolean))].sort();

    // Auto-sélection du display id s'il n'y en a qu'un (novice : pas de choix inutile).
    useEffect(() => {
        if (isVisible && !values.display_id && displayIds.length === 1) {
            setValues(v => ({ ...v, display_id: String(displayIds[0]) }));
        }
    }, [isVisible, displayIds.length]); // eslint-disable-line react-hooks/exhaustive-deps

    // eslint-disable-next-line no-unused-vars
    const orientation = (orientationMap && orientationMap[String(displayId)]) || 'paysage';

    const sameVideoIds = new Set(videoFiltered.filter(v => v.url && v.url === videoUrl).map(v => String(v.id)));
    const onThisVideo = allArticles.filter(a => sameVideoIds.has(String(a.display_id)));

    const maxX = Math.max(0, ...onThisVideo.map(a => num(a.position_x)), num(values.position_x));
    const designW = maxX > 55 ? 3840 : 1920;
    const is4K = designW >= 3840;

    useEffect(() => { setVideoErr(false); if (!is4K) setCropOn(false); }, [displayId, is4K]);

    // Écouteur clavier global tant que l'éditeur est ouvert (flèches, Ctrl+Z/Y, Échap).
    useEffect(() => {
        if (!isVisible) return;
        const h = (e) => {
            const tag = (e.target && e.target.tagName) || '';
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return; // saisie en cours
            if (onKeyRef.current) onKeyRef.current(e);
        };
        window.addEventListener('keydown', h);
        return () => window.removeEventListener('keydown', h);
    }, [isVisible]);

    useEffect(() => {
        if (!isVisible) return;
        const el = stageRef.current;
        if (!el) return;
        const measure = () => setStage({ w: el.clientWidth, h: el.clientHeight });
        measure();
        let ro;
        if (window.ResizeObserver) { ro = new ResizeObserver(measure); ro.observe(el); }
        window.addEventListener('resize', measure);
        return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', measure); };
    }, [isVisible, videoUrl]);

    if (!isVisible) return null;

    const m = metrics({ stageW: stage.w, stageH: stage.h, designW, crop: cropOn, quadrant });
    const contextPrices = onThisVideo.filter(a => String(a.Id) !== String(values.Id));
    const dirty = JSON.stringify(values) !== initialRef.current;

    // --- Historique (undo/redo) ---
    const pushHistory = () => { histRef.current.past.push(valuesRef.current); if (histRef.current.past.length > 60) histRef.current.past.shift(); histRef.current.future = []; };
    const set = (k, val) => { pushHistory(); setValues(v => ({ ...v, [k]: val })); };
    const undo = () => { const h = histRef.current; if (!h.past.length) return; h.future.push(valuesRef.current); setValues(h.past.pop()); };
    const redo = () => { const h = histRef.current; if (!h.future.length) return; h.past.push(valuesRef.current); setValues(h.future.pop()); };

    const requestClose = () => {
        if (dirty && !window.confirm('Modifications non enregistrées. Fermer sans enregistrer ?')) return;
        onClose();
    };

    /**
     * Sélectionne un prix DIRECTEMENT sur la vidéo (clic) et enchaîne le drag dans le même geste.
     * -> plus besoin de repasser par la liste pour choisir l'article : clic = sélection,
     *    glisser = déplacement, "Enregistrer" = confirmation.
     */
    const selectAndDrag = (e, p) => {
        e.preventDefault();
        e.stopPropagation();
        if (dirty && !window.confirm('Modifications non enregistrées sur le prix courant. Basculer sur cet autre prix ?')) return;
        const next = { ...BLANK, ...p };
        histRef.current = { past: [], future: [] };
        initialRef.current = JSON.stringify(next);
        setValues(next);
        setEditMode('edit'); // même ouvert en "create", on édite désormais un article existant
        startDrag(e, 'label', true); // skipHistory : l'historique vient d'être réinitialisé
    };

    const startDrag = (e, kind, skipHistory = false) => {
        e.preventDefault();
        e.stopPropagation();
        if (!skipHistory) pushHistory();
        const rect = stageRef.current.getBoundingClientRect();
        const step = grid ? 1 : 0.1;
        const move = (ev) => {
            let sx = Math.max(0, Math.min(rect.width, ev.clientX - rect.left));
            let sy = Math.max(0, Math.min(rect.height, ev.clientY - rect.top));
            const [px, py] = screenToPos(m, sx, sy);
            const rx = Math.round(px / step) * step, ry = Math.round(py / step) * step;
            const rxx = Math.round(rx * 10) / 10, ryy = Math.round(ry * 10) / 10;
            setValues(v => kind === 'icon'
                ? { ...v, position_icon_x: rxx, position_icon_y: ryy }
                : { ...v, position_x: rxx, position_y: ryy });
        };
        const up = () => { document.removeEventListener('pointermove', move); document.removeEventListener('pointerup', up); };
        document.addEventListener('pointermove', move);
        document.addEventListener('pointerup', up);
    };

    const onKey = (e) => {
        const ctrl = e.ctrlKey || e.metaKey;
        if (ctrl && (e.key === 'z' || e.key === 'Z')) { e.preventDefault(); e.shiftKey ? redo() : undo(); return; }
        if (ctrl && (e.key === 'y' || e.key === 'Y')) { e.preventDefault(); redo(); return; }
        if (e.key === 'Escape') { requestClose(); return; }
        if (!videoUrl) return;
        const d = e.shiftKey ? 1 : (grid ? 1 : 0.1);
        let handled = true;
        if (e.key === 'ArrowLeft') set('position_x', Math.round((num(values.position_x) - d) * 10) / 10);
        else if (e.key === 'ArrowRight') set('position_x', Math.round((num(values.position_x) + d) * 10) / 10);
        else if (e.key === 'ArrowUp') set('position_y', Math.round((num(values.position_y) - d) * 10) / 10);
        else if (e.key === 'ArrowDown') set('position_y', Math.round((num(values.position_y) + d) * 10) / 10);
        else handled = false;
        if (handled) e.preventDefault();
    };
    onKeyRef.current = onKey; // toujours la version du dernier render (state frais)

    const handleFontChange = (e) => {
        const val = e.target.value;
        if (val === '__add_font__') { if (fontFileRef.current) fontFileRef.current.click(); return; }
        set('font_name', val);
    };
    const handleFontFile = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        const slug = slugFont(file.name);
        setFontList(prev => prev.some(f => f.value === slug) ? prev : [...prev, { value: slug, label: file.name.replace(/\.(ttf|otf)$/i, '') }]);
        set('font_name', slug);
        toast.info(`Police « ${slug} » sélectionnée — le .ttf reste à envoyer au CMS puis aux clients (voir TODO fonts).`);
        e.target.value = '';
    };

    const headers = { 'x-access-token': token, 'Content-Type': 'application/json' };

    const persist = (url, payload, doneMsg, keepOpen) => {
        setSaving(true);
        axios.post(url, payload, { headers })
            .then((res) => {
                if (res.status === 200) {
                    return axios.post(`${IP}/retrivePinCode`, { idPdv }, { headers })
                        .catch(() => { })
                        .then(() => {
                            toast.success(doneMsg);
                            if (onSaved) onSaved();
                            initialRef.current = JSON.stringify(valuesRef.current);
                            if (!keepOpen) onClose();
                        });
                }
                toast.error('Réponse inattendue du serveur');
            })
            .catch(() => toast.error("Échec de l'enregistrement"))
            .finally(() => setSaving(false));
    };

    const save = () => {
        if (!values.name || values.price === '' || !values.display_id) {
            toast.error('Nom, prix et écran sont requis.');
            return;
        }
        const payload = { ...values, id_pdv: idPdv, client_id: clientId, value: idPdv, token };
        if (editMode === 'create') persist(`${IP2}/auth/user/createArticle/PriceEat`, payload, 'Article créé');
        else persist(`${IP2}/auth/user/updateArticle/PriceEat`, payload, 'Article enregistré');
    };
    const remove = () => {
        if (!window.confirm('Supprimer cet article ?')) return;
        persist(`${IP2}/auth/user/deleteArticle/PriceEat`, { Id: values.Id, id_pdv: idPdv }, 'Article supprimé');
    };
    const duplicate = () => {
        const payload = {
            ...values, Id: '', name: `${values.name} (copie)`,
            position_x: Math.round((num(values.position_x) + 2) * 10) / 10,
            position_y: Math.round((num(values.position_y) + 2) * 10) / 10,
            id_pdv: idPdv, client_id: clientId, value: idPdv, token,
        };
        persist(`${IP2}/auth/user/createArticle/PriceEat`, payload, 'Copie créée');
    };
    const applyStyleToAll = () => {
        if (!contextPrices.length) { toast.info('Aucun autre prix sur cet écran.'); return; }
        if (!window.confirm(`Appliquer la police, la taille et la couleur à ${contextPrices.length} autre(s) prix de cet écran ?`)) return;
        setSaving(true);
        Promise.all(contextPrices.map(a =>
            axios.post(`${IP2}/auth/user/updateArticle/PriceEat`,
                { ...a, font_name: values.font_name, font_size: values.font_size, font_color: values.font_color, id_pdv: idPdv, token },
                { headers }).catch(() => null)))
            .then(() => axios.post(`${IP}/retrivePinCode`, { idPdv }, { headers }).catch(() => { }))
            .then(() => { toast.success('Style appliqué à tous les prix de l\'écran'); if (onSaved) onSaved(); })
            .finally(() => setSaving(false));
    };

    const bumpSize = (delta) => set('font_size', String(Math.max(8, Math.min(200, num(values.font_size) + delta))));

    // Garde-fous
    const [ax, ay] = posToScreen(m, num(values.position_x), num(values.position_y));
    const activeVisible = inView(m, num(values.position_x), num(values.position_y)) && ax >= -4 && ay >= -4 && ax <= stage.w + 4 && ay <= stage.h + 4;
    const lightColor = luminance(values.font_color) > 0.7;

    const renderLabel = (p, active) => {
        if (!inView(m, num(p.position_x), num(p.position_y))) return null;
        const [x, y] = posToScreen(m, num(p.position_x), num(p.position_y));
        const showIcon = p.dispo !== 'yes' && p.editable_stock === 'yes' && inView(m, num(p.position_icon_x), num(p.position_icon_y));
        const [ix, iy] = showIcon ? posToScreen(m, num(p.position_icon_x), num(p.position_icon_y)) : [0, 0];
        const iconSz = num(p.width_icon) * m.fscale;
        const chrome = !preview; // Aperçu réel = pas de fond/contour éditeur
        return (
            <React.Fragment key={p.Id || 'active'}>
                <div
                    onPointerDown={active ? (e) => startDrag(e, 'label') : (e) => selectAndDrag(e, p)}
                    title={active ? p.name : `${p.name} — cliquer pour sélectionner / glisser pour déplacer`}
                    style={{
                        position: 'absolute', left: x, top: y, whiteSpace: 'nowrap', lineHeight: 1, minWidth: chrome ? 6 : 0, minHeight: chrome ? 6 : 0,
                        color: p.font_color || '#fff', fontFamily: p.font_name ? `'${p.font_name}', sans-serif` : undefined,
                        fontWeight: 700, textShadow: '0 1px 2px rgba(0,0,0,.9), 0 0 2px rgba(0,0,0,.7)',
                        cursor: active ? 'grab' : 'pointer', opacity: active ? 1 : (preview ? 1 : 0.9),
                        background: chrome ? (active ? 'rgba(99,102,241,.18)' : 'rgba(15,23,42,.22)') : 'transparent',
                        outline: chrome ? (active ? '1.5px solid #6366f1' : '1px dashed rgba(255,255,255,.65)') : 'none',
                        outlineOffset: 1, borderRadius: 3, touchAction: 'none',
                    }}
                >
                    <PriceLabel price={p.price} fscale={m.fscale} fontSize={p.font_size} />
                </div>
                {showIcon && (
                    <div
                        onPointerDown={active ? (e) => startDrag(e, 'icon') : undefined}
                        style={{
                            position: 'absolute', left: ix, top: iy, width: iconSz, height: iconSz,
                            display: 'grid', placeItems: 'center', color: '#fff', background: 'rgba(226,75,74,.9)',
                            borderRadius: 6, cursor: active ? 'grab' : 'default', opacity: active ? 1 : (preview ? 1 : 0.55),
                            outline: chrome && active ? '1.5px solid #6366f1' : 'none', outlineOffset: 2, touchAction: 'none',
                        }}
                    >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ width: '60%', height: '60%' }}>
                            <circle cx="12" cy="12" r="9" /><path d="M6 6l12 12" />
                        </svg>
                    </div>
                )}
            </React.Fragment>
        );
    };

    const inputCls = "w-full rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-white focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30";
    const labelCls = "mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400";
    const tglCls = (on) => `rounded-lg px-3 py-1.5 text-xs font-medium ${on ? 'bg-indigo-500 text-white' : 'border border-white/10 text-slate-300 hover:bg-white/5'}`;

    return (
        <div className="fixed inset-0 z-50 flex flex-col bg-slate-900/95 backdrop-blur-sm">
            {/* Barre supérieure */}
            <div className="flex h-14 shrink-0 items-center justify-between gap-3 border-b border-white/10 px-5">
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-white">{editMode === 'create' ? 'Nouvel article' : 'Éditer'}</span>
                    <div className="flex overflow-hidden rounded-lg border border-white/10">
                        <button onClick={() => setAdvanced(false)} className={`px-3 py-1.5 text-xs font-medium ${!advanced ? 'bg-indigo-500 text-white' : 'text-slate-300 hover:bg-white/5'}`}>Simple</button>
                        <button onClick={() => setAdvanced(true)} className={`px-3 py-1.5 text-xs font-medium ${advanced ? 'bg-indigo-500 text-white' : 'text-slate-300 hover:bg-white/5'}`}>Avancé</button>
                    </div>
                    <button onClick={() => setPreview(p => !p)} className={tglCls(preview)} title="Masquer les repères et voir le rendu final">Aperçu réel</button>
                    <button onClick={() => setGrid(g => !g)} className={tglCls(grid)} title="Grille magnétique (aligne au %)">Grille</button>
                    <div className="flex items-center gap-1">
                        <button onClick={undo} disabled={!histRef.current.past.length} className="grid h-7 w-7 place-items-center rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 disabled:opacity-40" title="Annuler (Ctrl+Z)">↺</button>
                        <button onClick={redo} disabled={!histRef.current.future.length} className="grid h-7 w-7 place-items-center rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 disabled:opacity-40" title="Rétablir (Ctrl+Y)">↻</button>
                    </div>
                    {is4K && (
                        <div className="flex overflow-hidden rounded-lg border border-white/10" title="Vue : écran entier ou zoom sur une dalle 2×2">
                            <button onClick={() => setCropOn(false)} className={`px-3 py-1.5 text-xs font-medium ${!cropOn ? 'bg-indigo-500 text-white' : 'text-slate-300 hover:bg-white/5'}`}>Écran entier</button>
                            {Q.map((q, i) => (
                                <button key={q} onClick={() => { setCropOn(true); setQuadrant(i); }} className={`px-2.5 py-1.5 text-xs font-medium ${cropOn && quadrant === i ? 'bg-indigo-500 text-white' : 'text-slate-300 hover:bg-white/5'}`}>{q}</button>
                            ))}
                        </div>
                    )}
                    <span className="text-xs text-slate-500">{videoNat.w ? `${videoNat.w}×${videoNat.h}` : ''}</span>
                </div>
                <button onClick={requestClose} className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-slate-300 hover:bg-white/10" aria-label="Fermer">✕</button>
            </div>

            <div className="flex min-h-0 flex-1">
                {/* Scène */}
                <div className="flex flex-1 items-center justify-center p-6">
                    {videoUrl ? (
                        <div ref={stageRef} className="relative w-full max-w-5xl overflow-hidden rounded-xl bg-neutral-800 shadow-2xl" style={{ aspectRatio: '16 / 9' }}>
                            <video key={videoUrl} src={videoUrl} autoPlay muted loop playsInline
                                onLoadedMetadata={e => { setVideoNat({ w: e.target.videoWidth, h: e.target.videoHeight }); setVideoErr(false); }}
                                onError={() => setVideoErr(true)}
                                className="absolute inset-0 h-full w-full object-contain"
                                style={m.crop ? quadrantVideoTransform(quadrant) : undefined} />
                            {grid && !preview && (
                                <div className="pointer-events-none absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.12) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.12) 1px,transparent 1px)', backgroundSize: '10% 10%' }} />
                            )}
                            {videoErr && (
                                <div className="pointer-events-none absolute inset-0 grid place-items-center p-4 text-center text-sm text-amber-300">
                                    Aperçu vidéo indisponible (URL/CORS). Les positions restent éditables.
                                </div>
                            )}
                            {stage.w > 0 && (
                                <>
                                    {contextPrices.map(p => renderLabel(p, false))}
                                    {renderLabel(values, true)}
                                </>
                            )}
                            {m.crop && !preview && (
                                <div className="pointer-events-none absolute left-2 top-2 rounded bg-slate-900/70 px-2 py-1 text-[11px] font-semibold text-white">Dalle {Q[quadrant]}</div>
                            )}
                        </div>
                    ) : (
                        <div className="max-w-md text-center text-slate-400">
                            {displayId ? (
                                <p className="text-sm">Aucune vidéo trouvée pour l'écran <b className="text-amber-300">{String(displayId)}</b>. Le player n'est probablement pas lié à ce PDV.</p>
                            ) : (
                                <p className="text-sm">Choisis un <b className="text-slate-200">écran</b> dans le panneau pour afficher la vidéo et placer les prix.</p>
                            )}
                        </div>
                    )}
                </div>

                {/* Inspecteur */}
                <div className="w-96 shrink-0 overflow-y-auto border-l border-white/10 bg-slate-900 p-5">
                    <div className="space-y-4">
                        {displayIds.length > 1 && (
                            <div>
                                <label className={labelCls}>Écran (display id)</label>
                                <select value={values.display_id} onChange={e => set('display_id', e.target.value)} className={inputCls}>
                                    <option value="">Choisir un écran</option>
                                    {displayIds.map(id => <option key={id} value={id}>{id}</option>)}
                                </select>
                            </div>
                        )}
                        <div>
                            <label className={labelCls}>Nom de l'article</label>
                            <input className={inputCls} value={values.name} onChange={e => set('name', e.target.value)} placeholder="Ex. Menu Burger" />
                        </div>
                        <div>
                            <label className={labelCls}>Prix (€)</label>
                            <input type="number" step="0.10" className={inputCls} value={values.price} onChange={e => set('price', e.target.value)} placeholder="0.00" />
                        </div>

                        <div>
                            <div className="mb-1 flex items-center justify-between">
                                <label className={labelCls + ' mb-0'}>Taille du texte</label>
                                <span className="text-xs font-semibold text-indigo-300">{num(values.font_size)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <button onClick={() => bumpSize(-2)} className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-slate-600 text-lg leading-none text-slate-300 hover:bg-white/5" title="Réduire">−</button>
                                <input type="range" min="8" max="200" step="1"
                                    value={num(values.font_size)}
                                    onPointerDown={pushHistory}
                                    onChange={(e) => setValues(v => ({ ...v, font_size: e.target.value }))}
                                    className="h-2 flex-1 cursor-pointer accent-indigo-500" />
                                <button onClick={() => bumpSize(2)} className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-slate-600 text-lg leading-none text-slate-300 hover:bg-white/5" title="Agrandir">+</button>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className={labelCls}>Police</label>
                                <select value={fontList.some(f => f.value === values.font_name) ? values.font_name : (values.font_name || '')} onChange={handleFontChange} className={inputCls}>
                                    <option value="" disabled>Choisir…</option>
                                    {fontList.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                                    {values.font_name && !fontList.some(f => f.value === values.font_name) && <option value={values.font_name}>{values.font_name} (actuel)</option>}
                                    <option value="__add_font__">➕ Ajouter une police…</option>
                                </select>
                                <input type="file" accept=".ttf,.otf" ref={fontFileRef} style={{ display: 'none' }} onChange={handleFontFile} />
                            </div>
                            <div><label className={labelCls}>Couleur</label><input type="color" className="h-[38px] w-full rounded-lg border border-slate-600 bg-slate-800 p-1" value={values.font_color} onChange={e => set('font_color', e.target.value)} /></div>
                        </div>

                        <label className="flex items-center gap-2 text-sm text-slate-200"><input type="checkbox" className="h-4 w-4 accent-indigo-500" checked={values.dispo !== 'yes'} onChange={e => set('dispo', e.target.checked ? 'no' : 'yes')} />En rupture (affiche l'icône)</label>

                        <button onClick={applyStyleToAll} disabled={saving} className="w-full rounded-lg border border-white/10 px-3 py-2 text-xs font-medium text-slate-200 hover:bg-white/5 disabled:opacity-50" title="Applique police, taille et couleur aux autres prix de cet écran">
                            🎨 Appliquer ce style à tous les prix de l'écran
                        </button>

                        {/* Garde-fous */}
                        {(lightColor || !activeVisible) && (
                            <div className="space-y-1 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-300">
                                {lightColor && <div>⚠️ Couleur très claire — risque d'illisibilité sur un fond clair.</div>}
                                {!activeVisible && <div>⚠️ Ce prix est hors de la zone visible — glisse-le sur la vidéo.</div>}
                            </div>
                        )}

                        {/* Section avancée */}
                        {advanced && (
                            <div className="space-y-4 border-t border-white/10 pt-4">
                                <div className="grid grid-cols-3 gap-3">
                                    <div><label className={labelCls}>Taille exacte</label><input type="number" step="1" className={inputCls} value={values.font_size} onChange={e => set('font_size', e.target.value)} /></div>
                                    <div><label className={labelCls}>Position X</label><input type="number" step="0.1" className={inputCls} value={values.position_x} onChange={e => set('position_x', e.target.value)} /></div>
                                    <div><label className={labelCls}>Position Y</label><input type="number" step="0.1" className={inputCls} value={values.position_y} onChange={e => set('position_y', e.target.value)} /></div>
                                </div>
                                <div className="grid grid-cols-3 gap-3">
                                    <div><label className={labelCls}>Icône X</label><input type="number" step="0.1" className={inputCls} value={values.position_icon_x} onChange={e => set('position_icon_x', e.target.value)} /></div>
                                    <div><label className={labelCls}>Icône Y</label><input type="number" step="0.1" className={inputCls} value={values.position_icon_y} onChange={e => set('position_icon_y', e.target.value)} /></div>
                                    <div><label className={labelCls}>Icône px</label><input type="number" step="1" className={inputCls} value={values.width_icon} onChange={e => set('width_icon', e.target.value)} /></div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <label className="flex items-center gap-2 text-sm text-slate-200"><input type="checkbox" className="h-4 w-4 accent-indigo-500" checked={values.editable_stock === 'yes'} onChange={e => set('editable_stock', e.target.checked ? 'yes' : 'no')} />Stock éditable</label>
                                    <div>
                                        <label className={labelCls}>Prix éditable</label>
                                        <select value={values.editable_price} onChange={e => set('editable_price', e.target.value)} className={inputCls}>
                                            <option value="yes">Oui</option><option value="no">Non</option>
                                        </select>
                                    </div>
                                </div>
                                <div><label className={labelCls}>Nom indispo</label><input className={inputCls} value={values.indispo_name} onChange={e => set('indispo_name', e.target.value)} /></div>
                                <div>
                                    <label className={labelCls}>Menu lié</label>
                                    <select value={values.linked_article_id || ''} onChange={e => set('linked_article_id', e.target.value)} className={inputCls}>
                                        <option value="">Aucun</option>
                                        {allArticles.map(a => <option key={a.Id} value={a.Id}>{a.name}</option>)}
                                    </select>
                                </div>
                                <div className="rounded-lg bg-white/5 px-3 py-2 text-[11px] leading-relaxed text-slate-400">
                                    Player : entier <b className="text-slate-200">{num(values.font_size) / 2}px</b> · <span className="font-mono">left = x·38</span> · réf. {designW}px{cropOn ? ` · dalle ${Q[quadrant]}` : ''}
                                </div>
                            </div>
                        )}

                        {!advanced && (
                            <p className="rounded-lg bg-white/5 px-3 py-2 text-[11px] leading-relaxed text-slate-400">
                                Astuce : glisse le prix sur la vidéo, ou flèches pour ajuster. Passe en <b className="text-slate-200">Avancé</b> pour les positions exactes et l'icône rupture.
                            </p>
                        )}

                        <div className="flex flex-wrap items-center gap-2 pt-1">
                            <button onClick={save} disabled={saving} className="flex-1 rounded-lg bg-indigo-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-600 disabled:opacity-50">{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
                            {editMode === 'edit' && <button onClick={duplicate} disabled={saving} className="rounded-lg border border-white/10 px-3 py-2.5 text-sm font-medium text-slate-200 hover:bg-white/5 disabled:opacity-50" title="Créer une copie de cet article">Dupliquer</button>}
                            {editMode === 'edit' && <button onClick={remove} disabled={saving} className="rounded-lg bg-red-600 px-3 py-2.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50">Supprimer</button>}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
